import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  WarningAmber as WarningIcon,
  EventNote as EventNoteIcon,
  Percent as PercentIcon,
  DateRange as DateRangeIcon,
  FilterAltOff as FilterOffIcon,
  EventBusy as EventBusyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { fetchTeacherAttendanceReport } from "./api/teacherHrApi";
import { endOfDayISO, getStoredTeacherId, startOfDayISO } from "./helpers";
import "./teacherHr.css";

/* -------------------- constants -------------------- */

const COUNT_ORDER = ["present", "absent", "late", "half_day", "on_leave"];

const STATUS_MAP = {
  present: {
    hex: "#10b981",
    bg: "#d1fae5",
    icon: <CheckCircleIcon fontSize="small" />,
    label: "Present",
  },
  absent: {
    hex: "#ef4444",
    bg: "#fee2e2",
    icon: <CancelIcon fontSize="small" />,
    label: "Absent",
  },
  late: {
    hex: "#f59e0b",
    bg: "#fef3c7",
    icon: <WarningIcon fontSize="small" />,
    label: "Late",
  },
  half_day: {
    hex: "#8b5cf6",
    bg: "#ede9fe",
    icon: <AccessTimeIcon fontSize="small" />,
    label: "Half Day",
  },
  halfday: {
    hex: "#8b5cf6",
    bg: "#ede9fe",
    icon: <AccessTimeIcon fontSize="small" />,
    label: "Half Day",
  },
  leave: {
    hex: "#0ea5e9",
    bg: "#e0f2fe",
    icon: <EventNoteIcon fontSize="small" />,
    label: "Leave",
  },
  on_leave: {
    hex: "#0ea5e9",
    bg: "#e0f2fe",
    icon: <EventNoteIcon fontSize="small" />,
    label: "On Leave",
  },
};

const STATUS_TABS = [
  { key: "All", label: "All" },
  { key: "Present", label: "Present" },
  { key: "Absent", label: "Absent" },
  { key: "Late", label: "Late" },
  { key: "Half Day", label: "Half Day" },
  { key: "Leave", label: "On Leave" },
];

/* -------------------- helpers -------------------- */

function normalizeStatusKey(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function statusConfig(status) {
  const key = normalizeStatusKey(status);
  return (
    STATUS_MAP[key] || {
      hex: "#94a3b8",
      bg: "#f1f5f9",
      icon: <AccessTimeIcon fontSize="small" />,
      label: status || "Unknown",
    }
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "—";
  }
}

function formatDateFull(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return startOfDay(d);
}

function inclusiveDays(a, b) {
  const A = startOfDay(a).getTime();
  const B = startOfDay(b).getTime();
  return Math.max(1, Math.round((B - A) / (1000 * 60 * 60 * 24)) + 1);
}

function durationBetween(inIso, outIso) {
  if (!inIso || !outIso) return null;
  const a = new Date(inIso).getTime();
  const b = new Date(outIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  const mins = Math.round((b - a) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

/* -------------------- component -------------------- */

export default function AttendanceReport() {
  const teacherId = getStoredTeacherId();

  const [fromD, setFromD] = useState(() => daysAgo(6));
  const [toD, setToD] = useState(() => startOfDay(new Date()));
  const [statusTab, setStatusTab] = useState("All");

  /* ✅ Collapsible summary — default open */
  const [summaryOpen, setSummaryOpen] = useState(true);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    if (!teacherId) {
      toast.error("Teacher id not found. Please log in again.");
      return;
    }
    setLoading(true);
    try {
      const from = startOfDayISO(fromD);
      const to = endOfDayISO(toD);
      const res = await fetchTeacherAttendanceReport({
        teacherId,
        from,
        to,
      });
      setSummary(res?.data?.data || null);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load report",
      );
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [teacherId, fromD, toD]);

  useEffect(() => {
    if (teacherId) load();
  }, [teacherId, load]);

  const resetFilters = () => {
    setStatusTab("All");
    setFromD(daysAgo(6));
    setToD(startOfDay(new Date()));
  };

  const records = useMemo(() => summary?.records || [], [summary]);
  const counts = useMemo(() => summary?.counts || {}, [summary]);

  const countCards = useMemo(() => {
    if (!counts || Object.keys(counts).length === 0) return [];
    const seen = new Set();
    const ordered = [];

    COUNT_ORDER.forEach((k) => {
      const matchKey = Object.keys(counts).find(
        (ck) => normalizeStatusKey(ck) === k,
      );
      if (matchKey != null) {
        seen.add(matchKey);
        const s = statusConfig(matchKey);
        ordered.push({
          key: matchKey,
          label: s.label,
          value: counts[matchKey],
          hex: s.hex,
          bg: s.bg,
          icon: s.icon,
        });
      }
    });

    Object.entries(counts).forEach(([k, v]) => {
      if (seen.has(k)) return;
      const s = statusConfig(k);
      ordered.push({
        key: k,
        label: s.label,
        value: v,
        hex: s.hex,
        bg: s.bg,
        icon: s.icon,
      });
    });

    return ordered;
  }, [counts]);

  const visibleRecords = useMemo(() => {
    if (statusTab === "All") return records;
    const target = normalizeStatusKey(statusTab);
    return records.filter((r) => {
      const key = normalizeStatusKey(r.status);
      if (target === "leave") return key === "leave" || key === "on_leave";
      if (target === "half_day") return key === "half_day" || key === "halfday";
      return key === target;
    });
  }, [records, statusTab]);

  const tabCounts = useMemo(() => {
    const count = (k) =>
      records.filter((r) => normalizeStatusKey(r.status) === k).length;
    return {
      All: records.length,
      Present: count("present"),
      Absent: count("absent"),
      Late: count("late"),
      "Half Day": count("half_day") + count("halfday"),
      Leave: count("leave") + count("on_leave"),
    };
  }, [records]);

  const attendancePct = Number(summary?.attendancePercentage ?? 0);
  const pctColor =
    attendancePct >= 75
      ? "#10b981"
      : attendancePct >= 50
        ? "#f59e0b"
        : "#ef4444";
  const pctLabel =
    attendancePct >= 75 ? "Good" : attendancePct >= 50 ? "Average" : "Low";

  const rangeDays = inclusiveDays(fromD, toD);
  const rangeValid = startOfDay(fromD).getTime() <= startOfDay(toD).getTime();

  /* Table grows taller when summary is collapsed */
  const tableMaxHeight = summaryOpen ? 180 : 350;

  return (
    <Box
      className="orange-scrollBar"
      sx={{
        maxHeight: 600,
        overflow: "auto",

        bgcolor: "#f8fafc",
      }}
    >
      {/* -------------------- Header (with collapse toggle) -------------------- */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          mb: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "#0ea5e9",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(14,165,233,0.25)",
              flexShrink: 0,
            }}
          >
            <AssessmentIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              className="fw-bold"
              sx={{ lineHeight: 1.2 }}
            >
              Attendance Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Summary and detailed attendance records for the selected period
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Collapsed mini-summary chips (only visible when collapsed) */}
          {!summaryOpen && summary && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Chip
                size="small"
                icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                label={`WD ${summary.workingDays ?? "—"}`}
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4338ca",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 24,
                  "& .MuiChip-icon": { color: "#4338ca" },
                }}
              />
              <Chip
                size="small"
                icon={<PercentIcon sx={{ fontSize: 14 }} />}
                label={`${summary.attendancePercentage ?? 0}%`}
                sx={{
                  bgcolor: `${pctColor}15`,
                  color: pctColor,
                  fontWeight: 700,
                  fontSize: 11,
                  height: 24,
                  "& .MuiChip-icon": { color: pctColor },
                }}
              />
              {countCards.slice(0, 3).map((c) => (
                <Chip
                  key={c.key}
                  size="small"
                  label={`${c.label} ${c.value}`}
                  sx={{
                    bgcolor: c.bg,
                    color: c.hex,
                    fontWeight: 700,
                    fontSize: 11,
                    height: 24,
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Toggle button */}
          {summary && (
            <Tooltip
              title={summaryOpen ? "Collapse summary" : "Expand summary"}
            >
              <IconButton
                size="small"
                onClick={() => setSummaryOpen((v) => !v)}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                {summaryOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Collapsible summary grid */}
        <Collapse in={summaryOpen} timeout={250} unmountOnExit={false}>
          <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
            {!teacherId ? (
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid #fecaca",
                  bgcolor: "#fef2f2",
                }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <WarningIcon sx={{ color: "#ef4444" }} />
                    <Typography
                      variant="body2"
                      sx={{ color: "#b91c1c", fontWeight: 600 }}
                    >
                      Missing Teacher ID in storage. Please log in again to view
                      your report.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ) : summary ? (
              <Grid container spacing={1.5}>
                {/* Working Days */}
                <Grid item xs={6} sm={4} md={2}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                      height: "100%",
                      transition: "all .2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                        borderColor: "#6366f1",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 1.5,
                        "&:last-child": { pb: 1.5 },
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          bgcolor: "#eef2ff",
                          color: "#6366f1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            fontSize: 11,
                          }}
                        >
                          Working Days
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#6366f1",
                            lineHeight: 1.1,
                          }}
                        >
                          {summary.workingDays ?? "—"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Attendance % */}
                <Grid item xs={6} sm={4} md={2}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                      height: "100%",
                      transition: "all .2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                        borderColor: pctColor,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 1.5,
                        "&:last-child": { pb: 1.5 },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            bgcolor: `${pctColor}15`,
                            color: pctColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PercentIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                              fontSize: 11,
                            }}
                          >
                            Attendance
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: pctColor,
                              lineHeight: 1.1,
                            }}
                          >
                            {summary.attendancePercentage != null
                              ? `${summary.attendancePercentage}%`
                              : "—"}
                          </Typography>
                        </Box>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(attendancePct, 100)}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: "#f1f5f9",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: pctColor,
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          display: "block",
                          color: pctColor,
                          fontWeight: 700,
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {pctLabel}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Count cards */}
                {countCards.map((c) => (
                  <Grid item xs={6} sm={4} md={2} key={c.key}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: "1px solid #e5e7eb",
                        height: "100%",
                        transition: "all .2s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                          borderColor: c.hex,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 1.5,
                          "&:last-child": { pb: 1.5 },
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            bgcolor: c.bg,
                            color: c.hex,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {c.icon}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                              fontSize: 11,
                            }}
                          >
                            {c.label}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: c.hex,
                              lineHeight: 1.1,
                            }}
                          >
                            {c.value}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : null}
          </Box>
        </Collapse>
      </Card>

      {/* -------------------- Main Card (table) -------------------- */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <Box sx={{ p: 2, borderBottom: "1px solid #eef2f7", bgcolor: "#fff" }}>
          {/* Status pills */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}
          >
            {STATUS_TABS.map((t) => {
              const active = statusTab === t.key;
              const conf = t.key === "All" ? null : statusConfig(t.key);
              const hex = conf?.hex || "#0ea5e9";
              return (
                <Button
                  key={t.key}
                  size="small"
                  onClick={() => setStatusTab(t.key)}
                  disableRipple
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.5,
                    minWidth: "auto",
                    fontSize: 13,
                    border: "1px solid",
                    borderColor: active ? hex : "#e5e7eb",
                    color: active ? "#fff" : hex,
                    bgcolor: active ? hex : "transparent",
                    "&:hover": {
                      bgcolor: active ? hex : `${hex}15`,
                      borderColor: hex,
                    },
                  }}
                >
                  {t.label}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: active ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                      color: active ? "#fff" : "text.secondary",
                    }}
                  >
                    {tabCounts[t.key] ?? 0}
                  </Box>
                </Button>
              );
            })}
          </Stack>

          {/* Date range + actions */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <DateRangeIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  mr: 0.5,
                }}
              >
                Range
              </Typography>

              <DatePicker
                selected={fromD}
                onChange={(d) => d && setFromD(startOfDay(d))}
                selectsStart
                startDate={fromD}
                endDate={toD}
                maxDate={toD}
                dateFormat="dd MMM yyyy"
                wrapperClassName="d-inline-block"
                customInput={
                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      minWidth: 130,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    {formatDateShort(fromD)}
                  </Box>
                }
              />

              <Typography variant="body2" color="text.secondary">
                →
              </Typography>

              <DatePicker
                selected={toD}
                onChange={(d) => d && setToD(startOfDay(d))}
                selectsEnd
                startDate={fromD}
                endDate={toD}
                minDate={fromD}
                dateFormat="dd MMM yyyy"
                wrapperClassName="d-inline-block"
                customInput={
                  <Box
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      px: 1.25,
                      py: 0.5,
                      fontSize: 13,
                      fontWeight: 600,
                      bgcolor: "#fff",
                      minWidth: 130,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { borderColor: "primary.main" },
                    }}
                  >
                    {formatDateShort(toD)}
                  </Box>
                }
              />

              <Chip
                size="small"
                label={`${rangeDays} day${rangeDays > 1 ? "s" : ""}`}
                sx={{
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Stack>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1}>
              <Tooltip title="Reset filters">
                <span>
                  <IconButton
                    size="small"
                    onClick={resetFilters}
                    disabled={loading}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    <FilterOffIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? null : <RefreshIcon fontSize="small" />}
                onClick={load}
                disabled={loading || !teacherId}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 2,
                  boxShadow: "none",
                  bgcolor: "#0ea5e9",
                  "&:hover": { bgcolor: "#0284c7" },
                }}
              >
                {loading ? "Loading…" : "Refresh Report"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Table — grows taller when summary collapses */}
        <TableContainer
          className="orange-scrollBar"
          sx={{
            maxHeight: tableMaxHeight,
            overflow: "auto",
            width: "100%",
            transition: "max-height .25s ease",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{ width: "100%", minWidth: 800 }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#f8fafc",
                    color: "#475569",
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid #e5e7eb",
                    py: 1.25,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 170 }}>Date</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Check In</TableCell>
                <TableCell sx={{ minWidth: 130 }}>Check Out</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Duration</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Remark</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !rangeValid ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <WarningIcon
                      sx={{ fontSize: 44, color: "warning.main", mb: 1 }}
                    />
                    <Typography variant="body1" fontWeight={600}>
                      Invalid date range
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      “From” date must be earlier than or equal to “To” date.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : visibleRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 7 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      <EventBusyIcon
                        sx={{ fontSize: 34, color: "action.disabled" }}
                      />
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      No records in this range
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      Try widening the dates or switching the status filter.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={resetFilters}
                      sx={{ textTransform: "none", borderRadius: 1.5 }}
                    >
                      Reset filters
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRecords.map((r, idx) => {
                  const s = statusConfig(r.status);
                  const duration = durationBetween(
                    r.checkInTime,
                    r.checkOutTime,
                  );
                  return (
                    <TableRow
                      key={r._id || `${r.date}-${r.status}-${idx}`}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? "#fff" : "#fafbfc",
                        "&:hover": { bgcolor: "#f1f5f9" },
                        "& td": {
                          borderBottom: "1px solid #eef2f7",
                          py: 1.25,
                          verticalAlign: "middle",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 6,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: s.hex,
                              flexShrink: 0,
                            }}
                          />
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontSize: 13 }}
                            >
                              {formatDate(r.date)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: 11 }}
                            >
                              {formatDateFull(r.date)}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          icon={s.icon}
                          label={s.label}
                          sx={{
                            bgcolor: s.bg,
                            color: s.hex,
                            fontWeight: 700,
                            fontSize: 12,
                            height: 26,
                            border: `1px solid ${s.hex}30`,
                            "& .MuiChip-icon": { color: s.hex, ml: 0.5 },
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        {r.checkInTime ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#10b981",
                              fontSize: 13,
                            }}
                          >
                            {formatTime(r.checkInTime)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {r.checkOutTime ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#6366f1",
                              fontSize: 13,
                            }}
                          >
                            {formatTime(r.checkOutTime)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {duration ? (
                          <Chip
                            size="small"
                            label={duration}
                            sx={{
                              bgcolor: "#f1f5f9",
                              color: "#334155",
                              fontWeight: 700,
                              fontSize: 11,
                              height: 22,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title={r.remark || r.reason || ""}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: 13,
                              maxWidth: 260,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.remark || r.reason || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer summary */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #eef2f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <DateRangeIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Showing data for{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formatDate(fromD)}
              </Box>{" "}
              to{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formatDate(toD)}
              </Box>{" "}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 999,
                  bgcolor: "#e0f2fe",
                  color: "#0369a1",
                  fontWeight: 700,
                  fontSize: 11,
                }}
              >
                {rangeDays} day{rangeDays > 1 ? "s" : ""}
              </Box>
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {loading
              ? "Loading…"
              : `${visibleRecords.length} record${
                  visibleRecords.length !== 1 ? "s" : ""
                }`}
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}
